import { useEffect, useState } from 'react';
import type { Locale } from '../shared/types';
import { api } from './api';
import { Brand, LanguageSwitch, text } from './ui';

export function Unsubscribe({ locale, setLocale }: { locale: Locale; setLocale: (value: Locale) => void }) {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') || '');
  const [context, setContext] = useState<{ maskedEmail: string; unsubscribed: boolean } | null>(null);
  const [loading, setLoading] = useState(true), [busy, setBusy] = useState(false), [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    api<{ maskedEmail: string; unsubscribed: boolean }>('/api/public/email/unsubscribe/resolve', {
      method: 'POST', body: JSON.stringify({ token }), signal: controller.signal,
    }).then(setContext).catch(() => { if (!controller.signal.aborted) setError(true); }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [token]);
  async function confirm() {
    setBusy(true); setError(false);
    try {
      await api('/api/public/email/unsubscribe', { method: 'POST', body: JSON.stringify({ token, confirm: true, scope: 'global' }) });
      setContext(value => value ? { ...value, unsubscribed: true } : value);
    } catch { setError(true); } finally { setBusy(false); }
  }
  return <div className="empty-page unsubscribe-page">
    <header><Brand /><LanguageSwitch locale={locale} onChange={setLocale} /></header>
    <UnsubscribeContent locale={locale} context={context} loading={loading} busy={busy} error={error} onConfirm={() => void confirm()} />
  </div>;
}

export function UnsubscribeContent({ locale, context, loading, busy, error, onConfirm }: {
  locale: Locale; context: { maskedEmail: string; unsubscribed: boolean } | null;
  loading: boolean; busy: boolean; error: boolean; onConfirm: () => void;
}) {
  return <main className="unsubscribe-card">
      <h1>{text(locale, 'E-posta tercihleri', 'Email preferences')}</h1>
      {loading ? <p role="status">{text(locale, 'Bağlantı kontrol ediliyor…', 'Checking your link…')}</p> : <>
        {error && <p role="alert">{text(locale, 'İşlem tamamlanamadı. Lütfen geçerli bağlantınızla tekrar deneyin veya Echos Education ile iletişime geçin.', 'We could not complete this request. Please try your valid link again or contact Echos Education.')}</p>}
        {context && <>
          <p className="unsubscribe-address">{context.maskedEmail}</p>
          {context.unsubscribed ? <div role="status"><h2>{text(locale, 'Aboneliğiniz iptal edildi', 'You’re unsubscribed')}</h2><p>{text(locale, 'Bu portaldan mevcut ve gelecekteki etkinlikler için e-posta almayacaksınız.', 'You will not receive emails from this portal for current or future events.')}</p></div> : <>
            <p>{text(locale, 'Tüm Echos Events e-postalarından çıkmak istiyor musunuz? Bu tercih mevcut ve gelecekteki tüm etkinlikler için geçerlidir.', 'Would you like to stop all Echos Events emails? This applies to every current and future event.')}</p>
            <p>{text(locale, 'Katılım yanıtınız, misafirleriniz ve kişisel davet bağlantınız değişmez.', 'Your RSVP, additional guests and personal invitation link will not change.')}</p>
            <button className="primary-button" type="button" disabled={busy} onClick={onConfirm}>{busy ? text(locale, 'Kaydediliyor…', 'Saving…') : text(locale, 'Tüm Echos Events e-postalarından çık', 'Unsubscribe from all Echos Events emails')}</button>
          </>}
          {context.unsubscribed && <p>{text(locale, 'Katılım yanıtınız değişmedi. Davet bağlantınızı kullanmaya devam edebilirsiniz.', 'Your RSVP has not changed. You can still use your invitation link.')}</p>}
        </>}
        <p><a href="mailto:info@echosedu.com">{text(locale, 'Yardım için iletişime geçin', 'Contact us for help')}</a></p>
      </>}
    </main>;
}
